import { pointer, select, Selection } from 'd3-selection'
import { scaleLinear } from 'd3-scale'
import { format } from 'd3-format'
import { axisBottom, axisLeft } from 'd3-axis'
import { css } from 'emotion'
import { line } from 'd3-shape'
import { Delaunay } from 'd3-delaunay'

import Utils from 'live/lib/utils'

import dateService from 'live/services/date'
import {
    getHiddenCurves,
    getStorageKey,
    Connection,
    ConnectionFingerprintState,
    xMax
} from './connectionFingerprintReducer'

import BaseChart from '../common/BaseChart'
import { axisCss } from '../common/TrackChart'
import Tooltip, { getColor, secondsFormat } from './Tooltip'
import { lockCurve, unlockCurve } from './LockApi'
import { sort } from 'timsort'

const formatNumber = (number: number) => Utils.formatNumber(number, 0, 3)

const legendUpperHeader = {
    connection_data: {
        title: 'Connection Data',
        span: 7,
        formatter: formatNumber,
        coloredBorder: true,
        upperHeader: true
    },
    pre_connection: {
        title: 'Pre-Connection',
        span: 3,
        formatter: formatNumber,
        coloredBorder: true,
        upperHeader: true
    }
    // post_connection: {
    //     title: 'Post-Connection',
    //     span: 3,
    //     formatter: formatNumber,
    //     upperHeader: true
    // }
}

const legendHeader = {
    color: {
        title: '',
        formatter: (data, idx) => {
            return `<hr style="border: 2px solid ${getColor(idx)}"/>`
        }
    },
    locked: {
        title: '',
        formatter: (locked, idx) => {
            if (locked) {
                return `<i class="fas fa-lock"></i>`
            }

            return `<i class="fas fa-unlock"></i>`
        }
    },
    connection_start: {
        title: 'Start Time',
        formatter: v => dateService.formatDateTime(v)
    },
    depth_value: {
        title: 'Depth',
        formatter: formatNumber
    },
    duration: {
        title: 'Duration',
        formatter: v => `${Math.floor(v / 1000 / 60)}m${secondsFormat((v % 60000) / 1000)}s`
    },
    max_volume: {
        title: 'Max Volume',
        formatter: formatNumber
    },
    max_volume_time: {
        title: 'Max. Vol. Time',
        formatter: v => (v || v === 0 ? `${Math.floor(v / 1000 / 60)}m${secondsFormat((v % 60000) / 1000)}s` : '-'),
        coloredBorder: true
    },
    pre_flow_in: {
        title: 'Flow In',
        formatter: formatNumber
    },
    pre_spp: {
        title: 'SPP',
        formatter: formatNumber
    },
    pre_active_vol: {
        title: 'Active Vol.',
        formatter: formatNumber,
        coloredBorder: true
    },
    // post_flow_in: {
    //     title: 'Pump Rate',
    //     formatter: formatNumber
    // },
    // post_spp: {
    //     title: 'SPP',
    //     formatter: formatNumber
    // },
    // post_active_vol: {
    //     title: 'Active Vol.',
    //     formatter: formatNumber
    // }
}

export default class ConnectionFingerprintChart extends BaseChart<ConnectionFingerprintState> {
    private svg: SVGSVGElement | null
    private xAxis: Selection<SVGGElement, unknown, null, undefined>
    private data: Selection<SVGGElement, unknown, null, undefined>
    private yAxis: Selection<SVGGElement, unknown, null, undefined>

    private margin = {
        top: 5,
        left: 25,
        right: 60,
        bottom: 45
    }

    private xline: Selection<SVGLineElement, unknown, SVGGElement, undefined>
    private yline: Selection<SVGLineElement, unknown, SVGGElement, undefined>
    private point: Selection<SVGCircleElement, unknown, SVGGElement, undefined>
    private legend: any
    private tooltip: Tooltip | undefined

    private xMax = xMax

    init(): void {
        this.el.classed(style, true)

        const svg = this.el
            .append('svg')
            .classed('chart', true)
            .classed(axisCss, true)

        this.xAxis = svg.append('g').classed('xAxis', true)
        this.yAxis = svg.append('g').classed('yAxis', true)
        this.data = svg.append('g').classed('data', true)

        this.tooltip = new Tooltip(this.el)

        this.legend = this.el.append('div').classed('legend', true)
        // TODO: support PointService (using bisect)
        //
        // this.mouseoverPoint = (e: MouseEvent, chart: any): void => {
        //     if (e && e.target && chart !== this) {
        //         this.onMouseMove(e.target.x)
        //     }
        // }
        // this.leave = () => this.onMouseover()
        // PointService.addEventListener('mouseover', this.mouseoverPoint)
        // PointService.addEventListener('mouseleave', this.leave)
    }

    get dimensions(): [number, number] {
        const node = this.el.select('svg').node() as SVGGElement
        if (node) {
            const { width, height } = node.getBoundingClientRect()

            return [width, height]
        } else {
            return [0, 0]
        }
    }

    get innerDimensions(): [number, number] {
        const [width, height] = this.dimensions
        const innerWidth = width - this.margin.left - this.margin.right
        const innerHeight = height - this.margin.top - this.margin.bottom

        return [innerWidth, innerHeight]
    }

    render(state: ConnectionFingerprintState): void {
        const [width, height] = this.dimensions

        const yDomain = [0, 0]
        let maxIndex = 0
        let points = []

        const connections: Connection[] = Array.from(state.connections.values()).filter(connection =>
            connection.values.some(v => v[0] <= this.xMax && v[1] > 0)
        )
        sort(connections, (a, b) => b.connection_start - a.connection_start)

        this.renderLegend(state, connections)

        for (const connection of connections) {
            if (connection.disabled) continue

            // generate list of points to delaunay algorithm
            points = points.concat(connection.values.filter(v => v[0] <= this.xMax && v[1] >= 0))

            // get the maxIndex
            if (connection.maxIndex > maxIndex) {
                maxIndex = Math.min(connection.maxIndex, this.xMax)
            }

            // generate domain (min/max) for yAxis
            if (yDomain[1] < connection.extent[1]) yDomain[1] = connection.extent[1]
        }

        const [innerWidth, innerHeight] = this.innerDimensions

        const xScale = scaleLinear()
            .domain([0, maxIndex])
            .range([0, innerWidth])
        const yScale = scaleLinear()
            .domain(yDomain)
            .range([innerHeight, 0])

        const delaunay = Delaunay.from(
            points,
            d => xScale(d[0]),
            d => yScale(d[1])
        )

        // TODO: improve
        const timeFormat = (timestamp: number): string => {
            const minutes = Math.floor(timestamp / 1000 / 60)
            const seconds = secondsFormat((timestamp % 60000) / 1000)

            return `${minutes}:${seconds}`
        }

        this.yAxis
            .attr('transform', `translate(${this.margin.right} ${this.margin.top})`)
            .call(axisLeft(yScale))
            .call(sel =>
                sel
                    .selectAll('text.title')
                    .data([null])
                    .join('text')
                    .classed('title', true)
                    .attr('alignment-baseline', 'hanging')
                    .text(state.uom.value ?? '')
                    .attr('transform', `translate(-50 ${Math.round(this.innerDimensions[1] / 2)}) rotate(-90)`)
            )
        this.xAxis
            .attr('transform', `translate(${this.margin.right} ${height - this.margin.bottom})`)
            .call(axisBottom(xScale).tickFormat(timeFormat))
            .call(sel =>
                sel
                    .selectAll('text.title')
                    .data([null])
                    .join('text')
                    .classed('title', true)
                    .attr('alignment-baseline', 'hanging')
                    .text('minutes')
                    .attr('transform', `translate(${Math.round(this.innerDimensions[0] / 2)} 35)`)
            )

        const l = line()
            .x(d => xScale(d[0]))
            .y(d => yScale(d[1]))

        // render lines
        this.data
            .attr('transform', `translate(${this.margin.right} ${this.margin.top})`)
            .selectAll('path')
            .data(connections, (connection: Connection) => connection.connection_start)
            .join('path')
            .attr('stroke', (d, i) => (d.disabled ? 'transparent' : getColor(i)))
            .attr('fill', 'transparent')
            .attr('d', (connection: Connection) => l(connection.values.filter(v => v[0] <= this.xMax && v[1] >= 0)))

        this.xline = this.data
            .selectAll('line.x-line')
            .data([null])
            .join('line')
            .classed('x-line', true)
            .attr('stroke', '#ccc')
            .attr('shape-rendering', 'crispEdges')
            .attr('x1', 0)
            .attr('x2', 0)
            .attr('y1', 0)
            .attr('y2', innerHeight)

        this.point = this.data
            .selectAll('circle.marker')
            .data([null])
            .join('circle')
            .classed('marker', true)
            .attr('r', 3)
            .attr('cx', -10)
            .attr('cy', -10)

        this.yline = this.data
            .selectAll('line.y-line')
            .data([null])
            .join('line')
            .classed('y-line', true)
            .attr('stroke', '#ccc')
            .attr('shape-rendering', 'crispEdges')
            .attr('x1', 0)
            .attr('x2', innerWidth)
            .attr('y1', 0)
            .attr('y2', 0)

        // main rect, to capturate events.
        this.data
            .selectAll('rect')
            .data([null])
            .join('rect')
            .attr('width', innerWidth)
            .attr('height', innerHeight)
            .attr('fill', 'transparent')
            .raise()
            .on('mouseleave', () => {
                this.cleanupMouse(connections)
            })
            .on('mousemove', e => {
                const p = pointer(e)
                const x = Math.round(p[0]) + 0.5
                const y = Math.round(p[1]) + 0.5

                this.xline.attr('x1', x).attr('x2', x)
                this.yline.attr('y1', y).attr('y2', y)

                const found = delaunay.find(...p)
                if (found) {
                    const [x, y, connection_start, timestamp] = points[found]
                    const xx = xScale(x)
                    const yy = yScale(y)

                    const connection = state.connections.get(connection_start)
                    const index = connections.findIndex(c => c.connection_start == connection_start)

                    const color = getColor(index)

                    this.point
                        .attr('cx', xx - 0.5)
                        .attr('cy', yy - 0.5)
                        .attr('fill', color)

                    this.tooltip?.render(
                        xx + this.margin.left + 20,
                        yy + this.margin.top + 30,
                        x,
                        y,
                        color,
                        timestamp,
                        state.uom.value,
                        this.innerDimensions
                    )
                    this.highlightAll(timestamp)
                    this.highlightConnection(connections, connection)
                } else {
                    this.cleanupMouse(connections)
                }
            })
    }

    highlightConnection(connections: Connection[], current: Connection, highlightAll = false): void {
        if (highlightAll) {
            this.highlightAll(current.connection_start)
        }
        this.data
            .selectAll('path')
            .data(connections, (connection: Connection) => connection.connection_start)
            .join('path')
            .classed('disabled', connection => connection.connection_start != current.connection_start)

        this.el
            .select('div.legend')
            .selectAll('tbody tr')
            .data(connections, (connection: Connection) => connection.connection_start)
            .join('tr')
            .classed('selected', connection => connection.connection_start === current.connection_start)
    }

    toggleCurve(state: ConnectionFingerprintState, key): void {
        if (state.connections.has(key)) {
            const connection = state.connections.get(key)
            const curves = getHiddenCurves(this.widgetId, state.type)
            if (connection.disabled) {
                connection.disabled = false
                curves.delete(connection.connection_start)
            } else {
                connection.disabled = true
                curves.add(connection.connection_start)
            }
            localStorage.setItem(getStorageKey(this.widgetId, state.type), JSON.stringify([...curves]))

            this.render(state)
        }
    }

    cleanupMouse(connections: Connection[]): void {
        this.data
            .selectAll('path')
            .data(connections, (connection: Connection) => connection.connection_start)
            .join('path')
            .classed('disabled', false)

        this.el
            .select('div.legend')
            .selectAll('tbody tr')
            .data(connections, (connection: Connection) => connection.connection_start)
            .join('div')
            .classed('selected', false)
            .style('opacity', (connection: Connection) => (connection.disabled ? 0.3 : 1))

        this.el.select('div.connection-fingerprint-tooltip').style('visibility', 'hidden')

        this.onMouseover()
    }

    lock(connection: Connection, eventType: string): void {
        this.dispatch({
            type: 'lock',
            key: connection.connection_start,
            layer: 0
        })
        lockCurve(this.widgetId, eventType, connection)
    }

    unlock(connection: Connection, eventType: string): void {
        this.dispatch({
            type: 'unlock',
            key: connection.connection_start,
            layer: 0
        })
        unlockCurve(this.widgetId, eventType, connection)
    }

    onMouseover(): void {
        this.point.attr('cx', -100)
        this.xline.attr('x1', -100).attr('x2', -100)
        this.yline.attr('y1', -100).attr('y2', -100)
    }

    onMouseMove(x: number): void {
        // console.log('x:', x)
    }

    renderLegend(state: ConnectionFingerprintState, connections: Connection[]): void {
        const table = this.el
            .select('div.legend')
            .selectAll('table')
            .data([null])
            .join('table')
            .classed(tableStyle, true)

        table
            .selectAll('thead')
            .data([null])
            .join('thead')
            .selectAll('tr')
            .data([legendUpperHeader, legendHeader])
            .join('tr')
            .selectAll('th')
            .data(row =>
                Object.keys(row).map(key => {
                    return {
                        key,
                        data: row[key]
                    }
                })
            )
            .join('th')
            .attr('colspan', h => {
                return h.data.span || 1
            })
            .style('border-right', d => (d.data.coloredBorder ? '1px solid #dddee1' : ''))
            .style('padding-right', d => (d.data.coloredBorder ? '4px' : ''))
            .style('text-align', d => (d.data.upperHeader ? 'center' : ''))
            .html(h => (state.labels[h.key] || h.data.title) + (state.uom[h.key] ? `(${state.uom[h.key]})` : ''))

        table
            .selectAll('tbody')
            .data([null])
            .join('tbody')
            .selectAll('tr')
            .data(connections)
            .join('tr')
            .style('opacity', d => (d.disabled ? 0.3 : 1))
            .on('mouseenter', (e, connection: Connection) => {
                connection.disabled
                    ? this.cleanupMouse(connections)
                    : this.highlightConnection(connections, connection, true)
            })
            .on('mouseleave', () => this.cleanupMouse(connections))
            .on('click', (e: MouseEvent, connection: Connection): void => {
                if (e.target.className.match(/unlock/)) {
                    this.lock(connection, state.assetType)
                } else if (e.target.className.match(/lock/)) {
                    this.unlock(connection, state.assetType)
                } else {
                    this.toggleCurve(state, connection.connection_start)
                    this.cleanupMouse(connections)
                }
            })
            .selectAll('td')
            .data(connection => {
                return Object.keys(legendHeader).map(key => {
                    return legendHeader[key].formatter
                        ? legendHeader[key].formatter(connection[key], connections.indexOf(connection))
                        : connection[key]
                })
            })
            .join('td')
            .html(d => d)
    }

    destroy(): void {
        this.el.selectAll('svg,.legend').remove()
    }
}

const tableStyle = css`
    border: 1px solid;
    width: calc(100% - 5px);
    border-radius: 2px;
    border: 1px solid #dddee1;

    .dark-theme & {
        border-color: #172033;
        thead {
            background-color: #1b273e;
            color: #fff;
        }
        tr:hover,
        tr.selected {
            background-color: #253047;
        }
    }

    tr {
        line-height: 24px;
        font-size: 10px;
    }

    thead {
        background: #eee;

        tr {
            &:first-child {
                th {
                    &:not(&:first-child) {
                        border-bottom: 1px solid #dddee1;
                    }
                }
            }
        }

        th {
            white-space: nowrap;
            text-align: right;

            &:last-child {
                padding-right: 4px;
            }
        }
    }

    tbody {
        tr {
            cursor: pointer;
            font-size: 12px;
            font-weight: 400;

            td:first-child hr {
                white-space: nowrap;
                width: 8px;
                border-width: 0 0 3px 0;
                display: inline-block;
                margin: 2px;
            }

            td {
                text-align: right;
                padding: 2px;

                &:nth-child(7),
                &:nth-child(10) {
                    border-right: 1px solid #dddee1;
                }

                &:nth-child(7),
                &:nth-child(10),
                &:last-child {
                    padding-right: 4px;
                }
                i.fa-unlock {
                    opacity: 0;
                }
            }
            &:hover {
                i.fa-unlock {
                    opacity: 1;
                }
            }
        }

        tr.selected {
            background: #e1f0ff;
        }
    }
    color: #364159;
`

const style = css`
    display: flex;
    height: 100%;
    position: relative;
    flex-direction: column;

    div,
    svg {
        flex: 1;
    }

    .connection-fingerprint-tooltip {
        position: absolute;
        font-size: 12px;
        font-family: 'Lucida Grande', 'Lucida Sans Unicode', Arial, Helvetica, sans-serif;
        height: auto;
        border: 1px solid;
        padding: 5px;
        color: #333;
        font-size: 12px;
        white-space: nowrap;
        opacity: 0.9;
        visibility: hidden;

        .tooltip-contents .tooltip-data-value abbr {
            color: #333;
        }

        .tooltip-contents .tooltip-date-time,
        .tooltip-contents .tooltip-data-value,
        .tooltip-event-icon .tooltip-date-time {
            font-family: SourceSansPro-SemiBold;
            font-size: 13px;
            margin-bottom: 1px;
        }
    }

    svg.chart {
        g.data {
            path {
                stroke-width: 2px;
            }
            path.disabled {
                stroke-width: 1px;
                // opacity: 0.5;
            }
        }

        g.yAxis,
        g.xAxis {
            text.title {
                font-family: 'Lucida Grande', 'Lucida Sans Unicode', Arial, Helvetica, sans-serif;
                font-size: 12px;
                fill: rgb(153, 153, 153);
                text-anchor: middle;
            }
        }
    }

    .legend {
        flex: 0.8;
        overflow: auto;

        > div {
            border-left: 3px solid #fff;
            margin-top: 1px;
            font-size: 12px;
            padding-left: 4px;
            cursor: pointer;
            &:hover {
                background-color: #f1f1f1;
            }
        }
    }
`
